import { toast } from 'sonner';
import {
  CHECK_URL_EMBEDDABLE,
  type CheckUrlEmbeddableResult,
  type CheckUrlEmbeddableVariables,
} from '@scio3/graphql-client';
import { registerActionHandler } from '../action-registry';
import { getNested } from '../nested-keys';
import { SYNTHETIC_PREVIEW_BLOCKED } from '../synthetic-form-keys';

/**
 * `verifyEmbeddableUrl` — verifies that a user-entered URL can be embedded inside the webplayer
 * iframe (open-api's `checkUrlEmbeddable` → X-Frame-Options + CSP `frame-ancestors`). Mirrors
 * scio1's `checkHeaderUrl` (advancedWebViewModal).
 *
 * Reads the URL from the field the action is attached to (`ctx.field.key`) — generic across
 * manifests: `webPageUrl` (advanced-web-view), `webLink` (Website). Writes the embeddability result
 * to the synthetic `_previewBlocked` form flag (via `ctx.form`) so a `notice` field gated on
 * `showIf: { _previewBlocked: [true] }` can surface a "preview not available" message.
 *
 * Trigger modes (all supported by the config engine):
 *  - `trigger: 'manual'` (button click) — toasts success/failure for user feedback.
 *  - `trigger: 'change'` / `'blur'` / `'mount'` (runOnChange / runOnBlur / runOnMount auto-triggers) —
 *    SILENT; only updates the `_previewBlocked` flag (the notice is the feedback, not a toast). Website
 *    uses `runOnChange` (live while typing) + `runOnMount` (rehydrate the notice on edit-mode reopen).
 *  - `isSaveCheck` (runOnSave) — SILENT; returns `{ ok: false, reason }` to gate save (advanced-web-view).
 *
 * Manifest usage (Website — buttonless, debounced auto-check while typing):
 *   { key: 'webLink', type: 'string', validation: { pattern: '^https?://\\S+$' }, action: {
 *       label: 'Check preview', handler: 'verifyEmbeddableUrl', requires: ['webLink'],
 *       runOnChange: true, runOnMount: true, hideButton: true
 *   } }
 */
registerActionHandler(
  'verifyEmbeddableUrl',
  async ({ values, form, field, apolloClient, t, isSaveCheck, trigger }) => {
    // Silent for every non-manual trigger (auto change/blur + pre-save) — the result surfaces via
    // the `_previewBlocked` flag / save gate, not a per-keystroke toast.
    const quiet = !!isSaveCheck || (trigger != null && trigger !== 'manual');

    const url = ((getNested(values, field?.key ?? 'webPageUrl') as string) ?? '').trim();
    if (!url) {
      if (!quiet) toast.error(t('Please enter a URL first'));
      return;
    }

    const { data } = await apolloClient.query<
      CheckUrlEmbeddableResult,
      CheckUrlEmbeddableVariables
    >({
      query: CHECK_URL_EMBEDDABLE,
      variables: { url },
      fetchPolicy: 'no-cache',
    });

    const result = data?.checkUrlEmbeddable;
    const canEmbed = !!result?.canEmbed;
    const reason = result?.reason ?? t('URL cannot be embedded in an iframe');

    // Drive the notice: blocked ⇔ !canEmbed. Synthetic `_`-key → stripped from the save payload.
    form?.setValue(SYNTHETIC_PREVIEW_BLOCKED, !canEmbed);

    if (canEmbed) {
      if (!quiet) toast.success(t('URL can be embedded'));
      return { ok: true };
    }
    if (!quiet) toast.error(reason);
    return { ok: false, reason };
  },
);
